import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Toaster, toast } from "react-hot-toast";
import {
  Search,
  Plus,
  RefreshCw,
  Download,
  Filter,
  X
} from "lucide-react";

import Login from "./components/Login";
import Navbar from "./components/Navbar";
import MetricsGrid from "./components/MetricsGrid";
import SubscriptionTable from "./components/SubscriptionTable";
import SubscriptionModal from "./components/SubscriptionModal";

import "./App.css";

const API_BASE_URL = "/api/subscriptions";

export default function App() {

  const [isAuthenticated,setIsAuthenticated]=useState(false);

  const [subscriptions,setSubscriptions]=useState([]);

  const [loading,setLoading]=useState(false);

  const [saving,setSaving]=useState(false);

  const [filterView,setFilterView]=useState("all");

  const [search,setSearch]=useState("");

  const [typeFilter,setTypeFilter]=useState("all");

  const [showModal,setShowModal]=useState(false);

  const [editingSub,setEditingSub]=useState(null);

  const config={

    headers:{

      "x-api-key":"tally-whatsapp-connector",

      "Content-Type":"application/json"

    }

  };

  const normalizeSubscription=(sub)=>{

    let serials=[];

    if(Array.isArray(sub.serialNumbers))
      serials=sub.serialNumbers;

    else if(sub.serial_numbers)

      serials=String(sub.serial_numbers)

        .split(",")

        .map(x=>x.trim())

        .filter(Boolean);

    return{

      id:String(sub.id),

      clientName:sub.clientName||sub.client_name||"",

      type:sub.type||"Direct",

      whatsappUsername:

        sub.whatsappUsername||

        sub.whatsapp_username||

        "",

      sellingRate:Number(

        sub.sellingRate??

        sub.selling_rate??

        0

      ),

      renewalRate:Number(

        sub.renewalRate??

        sub.renewal_rate??

        0

      ),

      expiryDate:

        sub.expiryDate||

        sub.expiry_date,

      remark:

        sub.remark||

        sub.remarks||

        "",

      isSuspended:Boolean(

        sub.isSuspended===true||

        sub.isSuspended===1||

        sub.is_suspended===1

      ),

      serialNumbers:serials

    };

  };

  const fetchSubscriptions=async()=>{

    setLoading(true);

    try{

      const response=

        await axios.get(

          API_BASE_URL,

          config

        );

      const raw=

        Array.isArray(response.data)

          ? response.data

          : response.data.data || [];

      setSubscriptions(

        raw.map(normalizeSubscription)

      );

    }

    catch(error){

      console.error(error);

      toast.error(

        "Unable to load subscriptions."

      );

    }

    finally{

      setLoading(false);

    }

  };

  useEffect(()=>{

    if(isAuthenticated){

      fetchSubscriptions();

    }

  },[isAuthenticated]);

  const filteredSubscriptions=useMemo(()=>{

    const now=new Date();

    const next30=new Date();

    next30.setDate(

      next30.getDate()+30

    );

    return subscriptions.filter(sub=>{

      const expiry=new Date(

        sub.expiryDate

      );

      if(filterView==="active"){

        if(sub.isSuspended) return false;

        if(expiry<now) return false;

        if(expiry<=next30) return false;

      }

      if(filterView==="expired"){

        if(expiry>=now)

          return false;

      }

      if(filterView==="expiringSoon"){

        if(sub.isSuspended)

          return false;

        if(expiry<now)

          return false;

        if(expiry>next30)

          return false;

      }

      if(filterView==="suspended"){

        if(!sub.isSuspended)

          return false;

      }

      if(

        typeFilter!=="all" &&

        sub.type!==typeFilter

      ){

        return false;

      }

      if(search.trim()){

        const keyword=

          search.toLowerCase();

        const matched=

          sub.clientName

            .toLowerCase()

            .includes(keyword)

          ||

          sub.type

            .toLowerCase()

            .includes(keyword)

          ||

          sub.whatsappUsername

            .toLowerCase()

            .includes(keyword)

          ||

          sub.serialNumbers.some(s=>

            s

            .toLowerCase()

            .includes(keyword)

          )

          ||

          String(sub.sellingRate)

            .includes(keyword)

          ||

          String(sub.renewalRate)

            .includes(keyword);

        if(!matched)

          return false;

      }

      return true;

    });

  },[

    subscriptions,

    filterView,

    search,

    typeFilter

  ]);

    const handleSaveSubscription = async (formData) => {

    setSaving(true);

    try {

      if (editingSub) {

        await axios.put(

          `${API_BASE_URL}/${editingSub.id}`,

          formData,

          config

        );

        toast.success(
          "Subscription updated successfully."
        );

      } else {

        await axios.post(

          API_BASE_URL,

          formData,

          config

        );

        toast.success(
          "Subscription created successfully."
        );

      }

      setShowModal(false);

      setEditingSub(null);

      await fetchSubscriptions();

    } catch (error) {

      console.error(error);

      toast.error(

        error.response?.data?.message ||

        "Unable to save subscription."

      );

    } finally {

      setSaving(false);

    }

  };

  const toggleSuspension = async (id) => {

    try {

      const current = subscriptions.find(

        s => String(s.id) === String(id)

      );

      if (!current) return;

      await axios.put(

        `${API_BASE_URL}/${id}`,

        {

          ...current,

          isSuspended:

            current.isSuspended ? 0 : 1

        },

        config

      );

      toast.success(

        current.isSuspended

          ? "Subscription Activated"

          : "Subscription Suspended"

      );

      await fetchSubscriptions();

    } catch (error) {

      console.error(error);

      toast.error(

        "Unable to update status."

      );

    }

  };

  const handleDelete = async (id) => {

    if (

      !window.confirm(

        "Delete this subscription?"

      )

    )

      return;

    try {

      await axios.delete(

        `${API_BASE_URL}/${id}`,

        config

      );

      toast.success(

        "Subscription deleted."

      );

      await fetchSubscriptions();

    } catch (error) {

      console.error(error);

      toast.error(

        error.response?.data?.message ||

        "Unable to delete subscription."

      );

    }

  };

  const handleRefresh = () => {

    fetchSubscriptions();

  };

  const clearFilters = () => {

    setSearch("");

    setTypeFilter("all");

    setFilterView("all");

  };

  const handleExport = () => {

    toast(

      "Excel export will be available soon.",

      {

        icon: "📄"

      }

    );

  };

  const openNewModal = () => {

    setEditingSub(null);

    setShowModal(true);

  };

  const openEditModal = (subscription) => {

    setEditingSub(subscription);

    setShowModal(true);

  };

  if (!isAuthenticated) {

    return (

      <Login

        onLoginSuccess={() =>

          setIsAuthenticated(true)

        }

      />

    );

  }

  return (

    <>

      <Toaster

        position="top-right"

        toastOptions={{

          duration:3000

        }}

      />

      <Navbar

        onLogout={() =>

          setIsAuthenticated(false)

        }

        searchTerm={search}

        setSearchTerm={setSearch}

        onRefresh={handleRefresh}

      />

      <main className="dashboard-container">

        <MetricsGrid

          subscriptions={subscriptions}

          filterView={filterView}

          setFilterView={setFilterView}

        />

        <div className="toolbar-card">

          <div className="toolbar-left">

            <div className="toolbar-title">

              <Filter size={18} />

              <span>

                Filters

              </span>

            </div>

            <div className="toolbar-search">

              <Search size={18} />

              <input

                type="text"

                placeholder="Search subscriptions..."

                value={search}

                onChange={(e)=>

                  setSearch(

                    e.target.value

                  )

                }

              />

            </div>

            {/* <select

              className="toolbar-select"

              value={typeFilter}

              onChange={(e)=>

                setTypeFilter(

                  e.target.value

                )

              }

            >

              <option value="all">

                All Types

              </option>

              <option value="Direct">

                Direct

              </option>

              <option value="Partner">

                Partner

              </option>

            </select>

            <button

              className="secondary-btn"

              onClick={clearFilters}

            >

              <X size={16}/>

              Clear

            </button> */}

          </div>

          <div className="toolbar-right">

            {/* <button
              className="secondary-btn"
              onClick={handleRefresh}
            >

              <RefreshCw size={16} />

              Refresh

            </button>

            <button
              className="secondary-btn"
              onClick={handleExport}
            >

              <Download size={16} />

              Export

            </button> */}

            <button
              className="primary-btn"
              onClick={openNewModal}
            >

              <Plus size={18} />

              Add Subscription

            </button>

          </div>

        </div>

        {

          loading

          ?

          <div className="loading-card">

            <div className="loader"></div>

            <h3>

              Loading subscriptions...

            </h3>

            <p>

              Please wait while we fetch your latest data.

            </p>

          </div>

          :

          <SubscriptionTable

            filteredSubscriptions={filteredSubscriptions}

            onEdit={openEditModal}

            onDelete={handleDelete}

            onToggleSuspend={toggleSuspension}

          />

        }

      </main>

      {

        showModal && (

          <SubscriptionModal

            editingSub={editingSub}

            saving={saving}

            onClose={() => {

              setShowModal(false);

              setEditingSub(null);

            }}

            onSave={handleSaveSubscription}

          />

        )

      }

    </>

  );

}
